import {Button, Checkbox, Modal, Popover, Skeleton, Tooltip} from 'antd'
import {
    course_createCourseClass_Api,
    course_deleteCourseClass_Api,
    course_editCourseClass_Api,
    course_getCourseClassesByOwner_Api,
} from 'apis'
import {getId} from 'apis/model/base'
import {FullSideModalLayout} from 'components/Modal/SideModalLayout'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {BsLayoutThreeColumns} from 'react-icons/bs'
import {MdOutlineArrowForward} from 'react-icons/md'
import {BiSolidEdit, BiTrash} from 'react-icons/bi'
import {TbTableOptions} from 'react-icons/tb'
import {PiChecks, PiGraduationCap} from 'react-icons/pi'
import {useLocation} from 'react-use'
import {branch, compose, withProps} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {ClassActionTypes} from 'views/AppLocals/Course'
import {FetchClassById} from 'views/AppLocals/Course/components/FetchEntity'
import {CreateCourseClassModal, EditCourseClassModal} from 'views/AppLocals/Course/CourseClass/CourseClassForm'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify} from 'views/AppLocals/factory/createNotification'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey} from 'views/AppLocals/hooks/usePaginationHelper'
import ListHeader from 'views/AppLocals/Templates/items/ListHeader'
import {NullAsDefault} from 'views/Shared'
import classNames from 'classnames'
import LoadingBlock from 'views/Loading/LoadingBlock'
import CourseClassFilter from '../components/filters/CourseClassFilter'
import {getRouteSearchParams, useRouteSearchParam} from 'envs/_current/history'
import {useHistory} from 'modules/navigation/useRouter'
import {CourseClassSummaryPanel} from './CourseClassList/CourseClassSummaryPanel'
import {CourseClassPanelDetail} from './CourseClassList/CourseClassPanelDetail'
import {renderIf} from "../../../Shared";
import getTitle from "../../../../helpers/getTitle";

const COLUMN_DEFS = [
    {key: 'code',     label: 'class code', required: true,  defaultVisible: true,  width: 140},
    {key: 'classes',   label: 'classes',     required: true,  defaultVisible: true,  width: null},
    {key: 'teacher',  label: 'teacher',    required: false, defaultVisible: false, width: 160},
    {key: 'students', label: 'students',   required: false, defaultVisible: true,  width: 80},
]
const DEFAULT_VISIBLE = new Set(COLUMN_DEFS.filter(c => c.defaultVisible).map(c => c.key))
const ColVisContext = React.createContext({visibleCols: DEFAULT_VISIBLE, onToggleCol: () => {}})

const SHOW_SUMMARY = 'SHOW_SUMMARY'
const SHOW_DETAIL = 'SHOW_DETAIL'

const ClassTableRow = ({item, index, renderContextMenu, onRowClick, onSummaryClick, onAttendanceClick, onGradingClick}) => {
    const t = useTranslate()
    const {visibleCols} = useContext(ColVisContext)
    const {
        course,
        coordinators = [],
        number_of_students,
        course_class_idcode,
    } = item ?? {}
    const firstCoordinator = coordinators[0]

    return (
        <tr
            className={classNames(
                'border-b border-color-50 hover:bg-blue-50 cursor-pointer transition-colors',
                index % 2 !== 0 && 'bg-gray-50'
            )}
            onClick={() => onRowClick(item)}>
            <td className="px-3 py-2 whitespace-no-wrap">
                {renderIf(
                    course_class_idcode,
                    <span className="whitespace-no-wrap px-2 py-1 bg-gray-100 border border-gray-200 rounded text-xs font-mono font-medium text-color-200">
                        #{course_class_idcode}
                    </span>
                )}
            </td>
            <td className="px-3 py-2">
                <span className="block truncate text-sm font-medium text-blue-600 hover:text-primary">
                    {getTitle(item)}
                </span>
            </td>
            {visibleCols.has('teacher') && (
                <td className="px-3 py-2 whitespace-no-wrap">
                    {renderIf(
                        firstCoordinator,
                        <span className="text-sm text-color-100">
                            {firstCoordinator?.teacher_name}
                        </span>,
                        <span className="text-xs text-color-400 italic">{t('not update')}</span>
                    )}
                </td>
            )}
            {visibleCols.has('students') && (
                <td className="px-3 py-2 text-center">
                    <span className="text-sm font-medium text-color-100">
                        {number_of_students ?? 0}
                    </span>
                </td>
            )}
            <td className="px-2 py-2 text-right whitespace-no-wrap">
                <button
                    className="p-1 flex flex-center rounded-full hover:bg-indigo-100 text-indigo-400 hover:text-indigo-600 transition-colors"
                    onClick={(e) => {
                        e.stopPropagation()
                        onRowClick(item)
                    }}>
                    <MdOutlineArrowForward size={18}/>
                </button>
            </td>
            <td className="p-2 text-center whitespace-no-wrap">
                <Tooltip title={t('attendance')}>
                    <button
                        className="p-1 flex flex-center rounded-lg hover:bg-indigo-100 text-indigo-400 hover:text-indigo-600 transition-colors"
                        onClick={(e) => {
                            e.stopPropagation()
                            onAttendanceClick(item)
                        }}>
                        <PiChecks size={16}/>
                    </button>
                </Tooltip>
            </td>
            <td className="p-2 text-center whitespace-no-wrap">
                <Tooltip title={t('gradebook')}>
                    <button
                        className="p-1 flex flex-center rounded-lg hover:bg-purple-100 text-purple-400 hover:text-purple-600 transition-colors"
                        onClick={(e) => {
                            e.stopPropagation()
                            onGradingClick(item)
                        }}>
                        <PiGraduationCap size={16}/>
                    </button>
                </Tooltip>
            </td>
            <td className="p-2 text-center whitespace-no-wrap">
                <Tooltip title={t('final assessment')}>
                    <button
                        className="p-1 flex flex-center rounded-lg hover:bg-green-100 text-green-500 hover:text-green-700 transition-colors"
                        onClick={(e) => {
                            e.stopPropagation()
                            onSummaryClick(item)
                        }}>
                        <TbTableOptions size={16}/>
                    </button>
                </Tooltip>
            </td>
            <td
                className="p-2 text-center"
                onClick={(e) => e.stopPropagation()}>
                {renderContextMenu(item)}
            </td>
        </tr>
    )
}

const TableLoadingRows = () => {
    const {visibleCols} = useContext(ColVisContext)
    return (
        <>
            {[...Array(4)].map((_, i) => (
                <tr key={i} className="border-b border-color-50">
                    <td className="px-3 py-3">
                        <Skeleton.Input active size="small" style={{width: 80, height: 20}}/>
                    </td>
                    <td className="px-3 py-3">
                        <Skeleton.Input active size="small" style={{width: 220, height: 20}}/>
                    </td>
                    {visibleCols.has('teacher') && (
                        <td className="px-3 py-3">
                            <Skeleton.Input active size="small" style={{width: 120, height: 20, minWidth: 120}}/>
                        </td>
                    )}
                    {visibleCols.has('students') && (
                        <td className="px-3 py-3">
                            <Skeleton.Input active size="small" style={{width: 60, height: 20, minWidth: 60}}/>
                        </td>
                    )}
                    <td className="px-1 py-3">
                        <Skeleton.Input active size="small" style={{width: 30, height: 20, minWidth: 30}}/>
                    </td>
                    <td className="px-1 py-3">
                        <Skeleton.Input active size="small" style={{width: 30, height: 20, minWidth: 30}}/>
                    </td>
                    <td className="px-1 py-3">
                        <Skeleton.Input active size="small" style={{width: 30, height: 20, minWidth: 30}}/>
                    </td>
                </tr>
            ))}
        </>
    )
}

const ClassTableWrapper = ({children}) => {
    const t = useTranslate()
    const {visibleCols, onToggleCol} = useContext(ColVisContext)
    const optionalCols = COLUMN_DEFS.filter(c => !c.required)
    const visibleDefs = COLUMN_DEFS.filter(c => c.required || visibleCols.has(c.key))

    const colPickerContent = (
        <div className="flex flex-col gap-2 py-1">
            {optionalCols.map(col => (
                <Checkbox
                    key={col.key}
                    checked={visibleCols.has(col.key)}
                    onChange={() => onToggleCol(col.key)}>
                    <span className="text-sm">{t(col.label)}</span>
                </Checkbox>
            ))}
        </div>
    )

    return (
        <div className="overflow-x-auto">
            <table className="w-full table-fixed">
                <colgroup>
                    {visibleDefs.map(col => (
                        <col key={col.key} style={col.width ? {width: col.width} : {}}/>
                    ))}
                    <col style={{width: 40}}/>
                    <col style={{width: 40}}/>
                    <col style={{width: 40}}/>
                    <col style={{width: 40}}/>
                    <col style={{width: 40}}/>
                </colgroup>
                <thead>
                <tr className="background-100 border-b border-color-100 11-11">
                    {visibleDefs.map(col => (
                        <th
                            key={col.key}
                            className="px-3 py-2 text-left text-xs font-semibold text-color-300 uppercase tracking-wider whitespace-no-wrap">
                            {t(col.label)}
                        </th>
                    ))}
                    <th className="px-2 py-2">
                        <Popover
                            content={colPickerContent}
                            trigger="click"
                            placement="bottomRight">
                            <button className="mx-auto p-1 flex flex-center rounded-md hover:background-200 text-color-300 hover:text-color-100 transition-colors">
                                <BsLayoutThreeColumns size={14}/>
                            </button>
                        </Popover>
                    </th>
                    <th className="px-3 py-2"/>
                    <th className="px-3 py-2"/>
                    <th className="px-3 py-2"/>
                    <th className="px-3 py-2"/>
                </tr>
                </thead>
                <tbody className="background divide-y divide-color-50">
                {children}
                </tbody>
            </table>
        </div>
    )
}

const HEADER_TEXT_BY_REL = {
    'course-class': {title: 'classes', description: 'note course class description'},
    'course-class-timetable': {title: 'class timetable title', description: 'class timetable list description'},
}

const ClassListManager = ({
    owner_id,
    owner_type,
    defaultTab = 'schedule',
    filter_actions = {
        sortBy: 'sort-by',
        keyword: 'keyword',
        course_id: 'course-id',
    },
    ...props
}) => {
    const t = useTranslate()
    const location = useLocation()
    const history = useHistory()
    const rel = useRouteSearchParam('rel')
    const sortBy = useRouteSearchParam(filter_actions.sortBy)
    const course_id = useRouteSearchParam(filter_actions.course_id)
    const class_id_param = useRouteSearchParam('class-id')
    const class_tab_param = useRouteSearchParam('class-tab')

    const headerText = HEADER_TEXT_BY_REL[rel] || HEADER_TEXT_BY_REL['course-class']

    // class_id/tab trên URL cho phép reload/chia sẻ link giữ đúng panel + tab đang mở
    const syncDetailQuery = useCallback((classId, tab) => {
        const searchParams = getRouteSearchParams()
        classId ? searchParams.set('class-id', classId) : searchParams.delete('class-id')
        tab ? searchParams.set('class-tab', tab) : searchParams.delete('class-tab')
        history.replace({search: '?' + searchParams.toString()})
    }, [history])

    const [action, setAction] = useState(() =>
        class_id_param
            ? {type: SHOW_DETAIL, value: {id: class_id_param}, tab: class_tab_param || defaultTab}
            : {type: undefined, value: undefined}
    )
    const [refreshKey, setRefreshKey] = useState()
    const [visibleCols, setVisibleCols] = useState(DEFAULT_VISIBLE)

    const onToggleCol = useCallback((key) => {
        setVisibleCols(prev => {
            const next = new Set(prev)
            next.has(key) ? next.delete(key) : next.add(key)
            return next
        })
    }, [])

    const onError = notifyOnError(t)

    const reload = () => {
        setRefreshKey(Date.now())
        onCancel()
    }

    const {handleAsyncAction: onCreate} = useAsyncAction({
        apiInfo: course_createCourseClass_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.CREATE, t)
            reload()
        },
    })

    const {handleAsyncAction: onUpdate} = useAsyncAction({
        apiInfo: course_editCourseClass_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            reload()
        },
    })

    const {handleAsyncAction: onDelete} = useAsyncAction({
        apiInfo: course_deleteCourseClass_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.DELETE, t)
            reload()
        },
    })

    const confirmDelete = useCallback(
        (id) => {
            if (id) {
                showDeleteConfirmDialog({
                    translate: t,
                    title: 'delete class of course',
                    message: 'are you sure you want to delete this class of course',
                    onOk: () => onDelete({}, {id}),
                })
            }
        },
        [onDelete]
    )

    const handleAction = (type, params, tab) => {
        switch (type) {
            case SHOW_DETAIL:
                setAction({type, value: params, tab: tab || defaultTab})
                syncDetailQuery(getId(params), tab || defaultTab)
                break
            case SHOW_SUMMARY:
                setAction({type, value: params})
                break
            case ClassActionTypes.CLASS_UPDATE:
            case ClassActionTypes.CLASS_CREATE:
                setAction({type, value: params})
                break
            case ClassActionTypes.CLASS_DELETE:
                confirmDelete(getId(params))
                break
            default:
                break
        }
    }

    const onCancel = () => {
        setAction({})
        if (action?.type === SHOW_DETAIL) syncDetailQuery(undefined, undefined)
    }

    const renderContextMenu = useCallback(
        (item) =>
            createContextMenu(
                [
                    {
                        icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                        label: 'edit',
                        key: ClassActionTypes.CLASS_UPDATE,
                    },
                    {
                        icon: <BiTrash size={14} className="text-primary-300"/>,
                        label: 'delete',
                        key: ClassActionTypes.CLASS_DELETE,
                    },
                ],
                (key) => handleAction(key, item)
            ),
        []
    )

    const modal = useMemo(() => {
        const {id} = action?.value ?? {}

        if (action?.type === SHOW_SUMMARY) {
            const classId = getId(action.value)
            const classTitle = action.value?.title ?? ''
            return (
                <FullSideModalLayout
                    full={true}
                    title={
                        <div className="flex items-center gap-2">
                            <div className="p-3 flex flex-center bg-green-50 rounded-lg">
                                <TbTableOptions size={20} className="text-green-600"/>
                            </div>
                            <div className="py-2 flex flex-col flex-1 gap-1">
                                <span className="font-bold text-base text-color-000 tracking-wide">
                                    {t('final assessment')}
                                    {/*<span className="ml-2 px-2 py-0.5 bg-secondary-50 border border-secondary-400 rounded-full font-semibold text-xs text-secondary-600 tracking-wide">*/}
                                    {/*    {classTitle}*/}
                                    {/*</span>*/}
                                </span>
                                <span className="font-normal text-xs text-color-300 tracking-wider">
                                    {t('final assessment description')}
                                </span>
                            </div>
                        </div>
                    }
                    onCancel={onCancel}>
                    <CourseClassSummaryPanel course_class_id={classId} titleClass={classTitle}/>
                </FullSideModalLayout>
            )

        }

        if (action?.type === SHOW_DETAIL) {
            const classId = getId(action.value)
            return (
                <FullSideModalLayout
                    right={true}
                    title={
                        <div className="font-bold text-color-000 tracking-wide">
                            {t('class detail')}
                        </div>
                    }
                    onCancel={onCancel}>
                    <div className="p-3">
                        <CourseClassPanelDetail
                            class_id={classId}
                            owner_id={owner_id}
                            owner_type={owner_type}
                            initialTab={action.tab || 'schedule'}
                            onCancel={onCancel}
                            onTabChange={(tab) => syncDetailQuery(classId, tab)}
                        />
                    </div>
                </FullSideModalLayout>
            )
        }

        let title
        let Content = NullAsDefault
        let modal_params = {}
        let params = {}
        let Wrapper = ({children}) => children({item: {}})

        switch (action?.type) {
            case ClassActionTypes.CLASS_CREATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('create course class')}
                    </div>
                )
                params = {
                    onCancel,
                    onSubmit: (values) => {
                        const {course_id: cid, ...rest} = values ?? {}
                        onCreate({...rest}, {id: cid})
                    },
                }
                modal_params = {width: 720}
                Content = CreateCourseClassModal
                break
            case ClassActionTypes.CLASS_UPDATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('edit class')}
                    </div>
                )
                params = {
                    onCancel,
                    onSubmit: (values) => onUpdate(values, {id}),
                }
                modal_params = {width: 720}
                Content = EditCourseClassModal
                Wrapper = withProps({id})(FetchClassById)
                break
            default:
                return null
        }

        return (
            <Modal
                footer={null}
                title={title}
                onCancel={onCancel}
                open={!!action?.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <Wrapper>
                    {({item, isLoading}) => {
                        if (isLoading) return <LoadingBlock/>
                        return <Content {...params} {...{item, owner_id, owner_type}}/>
                    }}
                </Wrapper>
            </Modal>
        )
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, owner_id, owner_type])

    const refresh_deps = useMemo(
        () => [
            owner_id,
            owner_type,
            refreshKey,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [owner_id, owner_type, refreshKey, location.state]
    )

    const [pager_widget] = usePaginationHelper({
        filter_params: {owner_id, owner_type},
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onFilterChange({course_id})
            onSortChange(sortBy)
        },
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            renderLoading: () => <TableLoadingRows/>,
            renderItem: (item, index) => (
                <ClassTableRow
                    key={index}
                    item={item}
                    index={index}
                    renderContextMenu={renderContextMenu}
                    onRowClick={(i) => handleAction(SHOW_DETAIL, i)}
                    onSummaryClick={(i) => handleAction(SHOW_SUMMARY, i)}
                    onAttendanceClick={(i) => handleAction(SHOW_DETAIL, i, 'attendance')}
                    onGradingClick={(i) => handleAction(SHOW_DETAIL, i, 'grading')}
                />
            ),
            apiInfo: course_getCourseClassesByOwner_Api,
            query: bindQueryParams([{id: owner_id}, {type: owner_type}]),
            renderWrap: (child) => <ClassTableWrapper>{child}</ClassTableWrapper>,
            ...props,
        },
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        isToggleDefault: true,
        query_params: filter_actions,
        AdvancedFilter: CourseClassFilter,
        dependencies: [owner_id, owner_type],
        withKey: createPagerKey(refresh_deps),
    })

    return (
        <ColVisContext.Provider value={{visibleCols, onToggleCol}}>
            <div className="flex flex-1 flex-col space-y-3">
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex flex-col flex-1">
                        <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                            {t(headerText.title)}
                        </h2>
                        <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                        <div className="mt-1 mr-4 text-sm text-color-300 tracking-wider">
                            {t(headerText.description)}
                        </div>
                    </div>
                    <Button
                        color="primary" variant="solid"
                        className="px-6 py-2 font-semibold rounded-lg"
                        onClick={() => handleAction(ClassActionTypes.CLASS_CREATE)}>
                        {t('create')}
                    </Button>
                </div>
                {pager_widget}
                {modal}
                <div className="h-16"/>
            </div>
        </ColVisContext.Provider>
    )
}

const AdvancedHeader = withProps({
    description: 'note course class description',
})(ListHeader)

const CourseClassList = compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(ClassListManager)

export default CourseClassList
